import { Autocomplete, TextField } from "@mui/material"
import { useMemo } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useBoundValue } from "lib/@components/binding/use-bound-value"

export function LinkedMacrosInner({ macros = [], editMode = true }) {
    const { target } = useBoundContext()
    const [linkedIds, setLinkedIds] = useBoundValue("linked")
    const options = useMemo(() => buildLinkedOptions(macros, target?.id), [macros, target?.id])
    const selectedOptions = useMemo(() => resolveSelectedOptions(linkedIds ?? [], options), [linkedIds, options])

    return (
        <Autocomplete
            multiple
            options={options}
            getOptionLabel={getOptionLabel}
            value={selectedOptions}
            onChange={handleChange}
            isOptionEqualToValue={isOptionEqualToValue}
            disabled={!editMode}
            renderInput={renderInput}
            onKeyDown={stopEnterPropagation}
        />
    )

    function stopEnterPropagation(e) {
        if (e.key === "Enter") e.stopPropagation()
    }

    function getOptionLabel(option) {
        return option?.name || option?.id || ""
    }

    function isOptionEqualToValue(option, value) {
        return option?.id === value?.id
    }

    function renderInput(params) {
        return <TextField {...params} placeholder="Search macros to link" data-cy="macro-linked-input" />
    }

    function handleChange(_event, values) {
        const nextIds = []
        for (const entry of values ?? []) {
            if (!entry?.id) continue
            nextIds.push(entry.id)
        }
        setLinkedIds(nextIds)
    }
}

function buildLinkedOptions(macros, selfId) {
    const options = []
    for (const macro of macros ?? []) {
        if (!macro?.id) continue
        if (macro._deleted) continue
        if (macro.id === selfId) continue
        options.push(macro)
    }
    options.sort(compareMacro)
    return options
}

function resolveSelectedOptions(linkedIds, options) {
    const optionById = {}
    for (const option of options) {
        optionById[option.id] = option
    }
    const selected = []
    for (const linkedId of linkedIds) {
        const option = optionById[linkedId]
        if (!option) continue
        selected.push(option)
    }
    return selected
}

function compareMacro(left, right) {
    const leftLabel = left?.name ?? left?.id ?? ""
    const rightLabel = right?.name ?? right?.id ?? ""
    return leftLabel.localeCompare(rightLabel, undefined, { sensitivity: "base" })
}
